import React, { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { toast } from 'sonner';
import { Mail, Check, X, Loader2, Eye, EyeOff, Settings, Send, AlertCircle, ExternalLink } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MarketingEmailSettings = ({ open, onOpenChange }) => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [showSecret, setShowSecret] = useState(false);
  const [currentProvider, setCurrentProvider] = useState(null);
  const [activeTab, setActiveTab] = useState('sendgrid');
  
  const [sendgridConfig, setSendgridConfig] = useState({
    api_key: '',
    from_email: '',
    from_name: ''
  });
  
  const [sesConfig, setSesConfig] = useState({
    access_key: '',
    secret_key: '',
    region: 'us-east-1',
    from_email: '',
    from_name: ''
  });
  
  const [smtpConfig, setSmtpConfig] = useState({
    host: '',
    port: '587',
    username: '',
    password: '',
    use_tls: true,
    from_email: '',
    from_name: ''
  });

  useEffect(() => {
    if (open) {
      fetchCurrentConfig();
    }
  }, [open]);

  const fetchCurrentConfig = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${API_URL}/api/marketing/settings/email-provider?token=${token}`);
      const data = await res.json();
      
      if (data.success && data.provider) {
        setCurrentProvider(data.provider);
        setActiveTab(data.provider);
        
        // Populate form based on provider type
        if (data.provider === 'sendgrid') {
          setSendgridConfig({
            api_key: data.api_key || '',
            from_email: data.from_email || '',
            from_name: data.from_name || ''
          });
        } else if (data.provider === 'aws_ses') {
          setSesConfig({
            access_key: data.access_key || '',
            secret_key: data.secret_key || '',
            region: data.region || 'us-east-1',
            from_email: data.from_email || '',
            from_name: data.from_name || ''
          });
        } else if (data.provider === 'smtp') {
          setSmtpConfig({
            host: data.host || '',
            port: data.port?.toString() || '587',
            username: data.username || '',
            password: data.password || '',
            use_tls: data.use_tls !== false,
            from_email: data.from_email || '',
            from_name: data.from_name || ''
          });
        }
      }
    } catch (error) {
      console.error('Error fetching email config:', error);
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    let config = {};
    let providerType = activeTab;
    
    if (activeTab === 'sendgrid') {
      if (!sendgridConfig.api_key || !sendgridConfig.from_email) {
        toast.error('API Key and From Email are required');
        return;
      }
      config = { ...sendgridConfig };
    } else if (activeTab === 'ses') {
      if (!sesConfig.access_key || !sesConfig.secret_key || !sesConfig.from_email) {
        toast.error('Access Key, Secret Key, and From Email are required');
        return;
      }
      providerType = 'aws_ses';
      config = { ...sesConfig };
    } else if (activeTab === 'smtp') {
      if (!smtpConfig.host || !smtpConfig.username || !smtpConfig.from_email) {
        toast.error('Host, Username, and From Email are required');
        return;
      }
      config = {
        ...smtpConfig,
        port: parseInt(smtpConfig.port)
      };
    }
    
    try {
      setSaving(true);
      const res = await fetch(`${API_URL}/api/marketing/settings/email-provider?token=${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          provider: providerType,
          config
        })
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Email provider configured successfully');
        setCurrentProvider(providerType);
      } else {
        toast.error(data.error || 'Failed to save configuration');
      }
    } catch (error) {
      toast.error('Error saving configuration');
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    try {
      setTesting(true);
      const res = await fetch(`${API_URL}/api/marketing/settings/email-provider/test?token=${token}`, {
        method: 'POST'
      });
      
      const data = await res.json();
      
      if (data.success) {
        toast.success('Connection test successful!');
      } else {
        toast.error(data.error || 'Connection test failed');
      }
    } catch (error) {
      toast.error('Error testing connection');
    } finally {
      setTesting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Email Provider Settings
          </DialogTitle>
          <DialogDescription>
            Configure your email provider for sending marketing campaigns
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-accent" />
          </div>
        ) : (
          <div className="space-y-6">
            {currentProvider && (
              <Alert>
                <Check className="h-4 w-4" />
                <AlertDescription>
                  Currently configured: <strong className="capitalize">{currentProvider}</strong>
                </AlertDescription>
              </Alert>
            )}

            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="sendgrid">SendGrid</TabsTrigger>
                <TabsTrigger value="ses">AWS SES</TabsTrigger>
                <TabsTrigger value="smtp">Custom SMTP</TabsTrigger>
              </TabsList>

              {/* SendGrid Tab */}
              <TabsContent value="sendgrid" className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500">
                    Get your API key from{' '}
                    <a href="https://app.sendgrid.com/settings/api_keys" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline inline-flex items-center gap-1">
                      SendGrid Dashboard <ExternalLink className="w-3 h-3" />
                    </a>
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div>
                    <Label>API Key *</Label>
                    <div className="relative">
                      <Input
                        type={showSecret ? 'text' : 'password'}
                        placeholder="SG.xxxxxxxxxxxxx"
                        value={sendgridConfig.api_key}
                        onChange={(e) => setSendgridConfig({ ...sendgridConfig, api_key: e.target.value })}
                        className="pr-10"
                      />
                      <button
                        type="button"
                        onClick={() => setShowSecret(!showSecret)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>From Email *</Label>
                      <Input
                        type="email"
                        placeholder="noreply@yourdomain.com"
                        value={sendgridConfig.from_email}
                        onChange={(e) => setSendgridConfig({ ...sendgridConfig, from_email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>From Name</Label>
                      <Input
                        placeholder="Your Company"
                        value={sendgridConfig.from_name}
                        onChange={(e) => setSendgridConfig({ ...sendgridConfig, from_name: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* AWS SES Tab */}
              <TabsContent value="ses" className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-slate-500">
                    Get credentials from{' '}
                    <a href="https://console.aws.amazon.com/ses" target="_blank" rel="noopener noreferrer" className="text-accent hover:underline inline-flex items-center gap-1">
                      AWS SES Console <ExternalLink className="w-3 h-3" />
                    </a>
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Access Key ID *</Label>
                      <Input
                        placeholder="AKIAXXXXXXXX"
                        value={sesConfig.access_key}
                        onChange={(e) => setSesConfig({ ...sesConfig, access_key: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Secret Access Key *</Label>
                      <div className="relative">
                        <Input
                          type={showSecret ? 'text' : 'password'}
                          placeholder="xxxxxxxxxxxxxxxx"
                          value={sesConfig.secret_key}
                          onChange={(e) => setSesConfig({ ...sesConfig, secret_key: e.target.value })}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecret(!showSecret)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div>
                    <Label>Region</Label>
                    <Input
                      placeholder="us-east-1"
                      value={sesConfig.region}
                      onChange={(e) => setSesConfig({ ...sesConfig, region: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>From Email *</Label>
                      <Input
                        type="email"
                        placeholder="noreply@yourdomain.com"
                        value={sesConfig.from_email}
                        onChange={(e) => setSesConfig({ ...sesConfig, from_email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>From Name</Label>
                      <Input
                        placeholder="Your Company"
                        value={sesConfig.from_name}
                        onChange={(e) => setSesConfig({ ...sesConfig, from_name: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>

              {/* SMTP Tab */}
              <TabsContent value="smtp" className="space-y-4 mt-4">
                <p className="text-sm text-slate-500">
                  Configure any SMTP server (Microsoft 365, Gmail, custom server)
                </p>
                
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>SMTP Host *</Label>
                      <Input
                        placeholder="smtp.office365.com"
                        value={smtpConfig.host}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, host: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Port</Label>
                      <Input
                        placeholder="587"
                        value={smtpConfig.port}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, port: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>Username *</Label>
                      <Input
                        placeholder="your-email@domain.com"
                        value={smtpConfig.username}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, username: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>Password *</Label>
                      <div className="relative">
                        <Input
                          type={showSecret ? 'text' : 'password'}
                          placeholder="Password or App Password"
                          value={smtpConfig.password}
                          onChange={(e) => setSmtpConfig({ ...smtpConfig, password: e.target.value })}
                          className="pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowSecret(!showSecret)}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          {showSecret ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>From Email *</Label>
                      <Input
                        type="email"
                        placeholder="noreply@yourdomain.com"
                        value={smtpConfig.from_email}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, from_email: e.target.value })}
                      />
                    </div>
                    <div>
                      <Label>From Name</Label>
                      <Input
                        placeholder="Your Company"
                        value={smtpConfig.from_name}
                        onChange={(e) => setSmtpConfig({ ...smtpConfig, from_name: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
              </TabsContent>
            </Tabs>
          </div>
        )}

        <DialogFooter className="gap-2">
          {currentProvider && (
            <Button variant="outline" onClick={testConnection} disabled={testing}>
              {testing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Send className="w-4 h-4 mr-2" />}
              Test Connection
            </Button>
          )}
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={saveConfig} disabled={saving}>
            {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Check className="w-4 h-4 mr-2" />}
            Save Configuration
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default MarketingEmailSettings;
